"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { createPortal } from "react-dom";
import { BoltIcon } from "@heroicons/react/24/solid";
import { useHomeLanguage } from "./HomeLanguageContext";

const REGISTER_ENDPOINT = "https://demo.kolabo.id/api/company-register";
const REGISTER_SUCCESS_MESSAGE = "Data created successfully. Please verify your email.";
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "0x4AAAAAABsBNHaZIB_hjsLt";

const moduleOptions = ["HRM", "Accounting", "CRM", "Project Management", "POS", "Custom"] as const;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string | number;
      reset: (widgetId?: string | number) => void;
    };
    fbq?: (...args: unknown[]) => void;
    ttq?: { track: (eventName: string, data?: Record<string, unknown>) => void };
    gtag?: (...args: unknown[]) => void;
  }
}

export default function TrialForm() {
  const { lang } = useHomeLanguage();
  const [mounted, setMounted] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);
  const [form, setForm] = useState({
    namaLengkap: "",
    namaPerusahaan: "",
    email: "",
    whatsapp: "",
  });
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);

  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | number | null>(null);
  const previousBodyOverflowRef = useRef<string>("");

  useEffect(() => {
    setMounted(true);
  }, []);

  const openRegisterPopup = useCallback(() => {
    setShowRegisterPopup(true);
    if (window.location.hash.toLowerCase() !== "#daftar") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#daftar`);
    }
  }, []);

  const closeRegisterPopup = useCallback(() => {
    setShowRegisterPopup(false);
    setErrorMessage("");
    if (window.location.hash.toLowerCase() === "#daftar") {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  useEffect(() => {
    const syncByHash = () => {
      if (window.location.hash.toLowerCase() === "#daftar") {
        setShowRegisterPopup(true);
      } else {
        setShowRegisterPopup(false);
      }
    };

    syncByHash();
    window.addEventListener("hashchange", syncByHash);
    return () => window.removeEventListener("hashchange", syncByHash);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest("a[href='#daftar']");
      if (!target) return;
      event.preventDefault();
      openRegisterPopup();
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [openRegisterPopup]);

  useEffect(() => {
    if (!showRegisterPopup) return;
    previousBodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflowRef.current;
    };
  }, [showRegisterPopup]);

  useEffect(() => {
    if (!showRegisterPopup) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRegisterPopup();
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [showRegisterPopup, closeRegisterPopup]);

  const renderTurnstile = useCallback(() => {
    if (!showRegisterPopup || !turnstileLoaded || !window.turnstile || !turnstileRef.current) {
      return;
    }
    if (turnstileWidgetIdRef.current !== null) {
      return;
    }

    turnstileWidgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "light",
      callback: (token) => {
        setTurnstileToken(token);
        setErrorMessage("");
      },
      "expired-callback": () => {
        setTurnstileToken("");
      },
      "error-callback": () => {
        setTurnstileToken("");
      },
    });
  }, [showRegisterPopup, turnstileLoaded]);

  useEffect(() => {
    renderTurnstile();
  }, [renderTurnstile]);

  useEffect(() => {
    if (showRegisterPopup) return;
    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
    turnstileWidgetIdRef.current = null;
    setTurnstileToken("");
  }, [showRegisterPopup]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errorMessage) setErrorMessage("");
  };

  const validateForm = () => {
    if (!form.namaPerusahaan.trim()) return lang === "id" ? "Nama perusahaan wajib diisi." : "Company name is required.";
    if (!form.email.trim()) return lang === "id" ? "Email wajib diisi." : "Email is required.";
    if (!form.namaLengkap.trim()) return lang === "id" ? "Nama PIC wajib diisi." : "PIC name is required.";
    if (!form.whatsapp.trim()) return lang === "id" ? "Nomor telepon wajib diisi." : "Phone number is required.";
    if (!turnstileToken) return lang === "id" ? "Captcha diperlukan. Mohon centang/verifikasi dulu." : "Captcha is required. Please complete verification first.";
    return "";
  };

  const toggleModule = (module: string) => {
    setSelectedModules((prev) =>
      prev.includes(module) ? prev.filter((item) => item !== module) : [...prev, module]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    let isSuccess = false;

    try {
      const payload = new URLSearchParams();
      payload.append("company", form.namaPerusahaan.trim());
      payload.append("email", form.email.trim());
      payload.append("pic_name", form.namaLengkap.trim());
      payload.append("phone", form.whatsapp.trim());
      payload.append("cf-turnstile-response", turnstileToken);
      payload.append("interested_modules", selectedModules.join(","));
      selectedModules.forEach((module) => payload.append("interested_modules[]", module));

      const response = await fetch(REGISTER_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let responseBody: { message?: string } = {};

      try {
        responseBody = JSON.parse(responseText) as { message?: string };
      } catch {
        responseBody = {};
      }

      const message = responseBody?.message || "";

      if (!response.ok) {
        throw new Error(message || responseText || "Terjadi kesalahan.");
      }

      if (message === "The company format is invalid.") {
        setErrorMessage(
          lang === "id"
            ? 'Error: Nama perusahaan hanya boleh berisi huruf, angka, ".", atau ",".'
            : 'Error: Company name can contain only letters, numbers, ".", or ",".'
        );
        return;
      }

      if (message !== REGISTER_SUCCESS_MESSAGE) {
        setErrorMessage(message || (lang === "id" ? "Terjadi kesalahan saat proses pendaftaran." : "An error occurred during registration."));
        return;
      }

      setSubmitted(true);
      isSuccess = true;

      try {
        if (typeof window.fbq === "function") {
          const metaLeadPayload = {
            content_name: "Kolabo Trial Registration",
            content_category: "Lead Form",
            status: "success",
            source: "landing_page",
          };

          // Standard Meta conversion event for lead optimization.
          window.fbq("track", "Lead", metaLeadPayload);

          // Keep existing custom flow/event mapping if already used in campaigns.
          window.fbq("track", "StartTrial", metaLeadPayload);
        }
      } catch {
        // noop
      }

      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-16564601163/IeSwCNvL8aAcEMuCz9o9",
          });
        }
      } catch {
        // noop
      }

      try {
        if (window.ttq?.track) {
          window.ttq.track("CompleteRegistration", {});
        }
      } catch {
        // noop
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : lang === "id" ? "Terjadi kesalahan." : "An error occurred.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
      if (!isSuccess && window.turnstile && turnstileWidgetIdRef.current !== null) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        setTurnstileToken("");
      }
    }
  };

  const modalContent = showRegisterPopup ? (
    <div className="fixed inset-0 z-[1900] flex items-center justify-center p-3 sm:p-5">
      <div
        className="absolute inset-0 bg-fg/60"
        onClick={closeRegisterPopup}
        aria-hidden="true"
      />
      <div
        id="daftar"
        className="relative z-[1910] w-[min(96vw,840px)] max-h-[92vh] overflow-y-auto rounded-xl sm:rounded-2xl bg-white border border-stone-200 shadow-glow-md"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-brand" />
        <button
          type="button"
          onClick={closeRegisterPopup}
          className="absolute right-3 top-3 w-9 h-9 rounded-full border border-stone-200 bg-white text-fg/70 hover:text-fg hover:border-primary/25"
          aria-label={lang === "id" ? "Tutup form daftar" : "Close register form"}
        >
          ×
        </button>

        {submitted ? (
          <div className="text-center py-14 px-5 sm:px-8">
            <div className="w-20 h-20 rounded-full bg-gradient-brand flex items-center justify-center mx-auto mb-6 shadow-brand-lg">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h3 className="font-heading text-2xl font-bold text-fg mb-3 tracking-tight">
              {lang === "id" ? "Pendaftaran Berhasil!" : "Registration Successful!"}
            </h3>
            <p className="text-fg-muted max-w-sm mx-auto text-sm leading-relaxed">
              {lang === "id"
                ? "Akun berhasil dibuat. Silakan cek email Anda untuk verifikasi dan informasi akun Kolabo."
                : "Your account has been created successfully. Please check your email for verification and account details."}
            </p>
          </div>
        ) : (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6 sm:mb-7">
              <h3 className="font-heading text-xl font-bold text-fg mb-1.5 tracking-tight">
                {lang === "id" ? "Daftar Kolabo" : "Register Kolabo"}
              </h3>
              <p className="text-fg-muted text-sm">
                {lang === "id" ? (
                  <>
                    Hanya butuh beberapa detik untuk mencoba semua fitur Kolabo,{" "}
                    <strong className="font-semibold text-fg">tanpa ada kewajiban berlangganan.</strong>
                  </>
                ) : (
                  "An active WhatsApp number and email are required for account verification."
                )}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              {errorMessage && (
                <p className="text-red-600 text-sm leading-relaxed bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {errorMessage}
                </p>
              )}

              {(
                lang === "id"
                  ? [
                      { id: "namaLengkap", label: "Nama Lengkap", type: "text", placeholder: "Masukkan nama lengkap Anda" },
                      { id: "namaPerusahaan", label: "Nama Perusahaan / Bisnis", type: "text", placeholder: "Nama perusahaan atau bisnis Anda" },
                      { id: "email", label: "Email Aktif", type: "email", placeholder: "Email perusahaan / pribadi" },
                      { id: "whatsapp", label: "Nomor WhatsApp Aktif", type: "tel", placeholder: "08xxxxxxxxxx" },
                    ]
                  : [
                      { id: "namaLengkap", label: "Full Name", type: "text", placeholder: "Enter your full name" },
                      { id: "namaPerusahaan", label: "Company / Business Name", type: "text", placeholder: "Your company or business name" },
                      { id: "email", label: "Active Email", type: "email", placeholder: "email@company.com" },
                      { id: "whatsapp", label: "Active WhatsApp Number", type: "tel", placeholder: "+62xxxxxxxxxx" },
                    ]
              ).map((field) => (
                <div key={field.id}>
                  <label htmlFor={field.id} className="block text-[11px] sm:text-xs font-bold text-fg/50 uppercase tracking-[0.12em] sm:tracking-[0.14em] mb-2">
                    {field.label} <span className="text-red-500">*</span>
                  </label>
                  <input
                    id={field.id}
                    name={field.id}
                    type={field.type}
                    required
                    value={form[field.id as keyof typeof form]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    className="form-input w-full px-4 py-2.5 sm:py-3 rounded-lg border border-stone-200 bg-stone-50 text-fg placeholder-stone-400 text-sm"
                  />
                </div>
              ))}

              <div className="pb-1">
                <p className="text-[12px] text-fg-muted mb-3">
                  {lang === "id"
                    ? "Pilih modul yang Anda butuhkan (bisa lebih dari satu)."
                    : "Pick modules that You want (can multiple)."}
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {moduleOptions.map((module) => {
                    const active = selectedModules.includes(module);
                    return (
                      <button
                        key={module}
                        type="button"
                        onClick={() => toggleModule(module)}
                        className={`relative inline-flex items-center gap-2 rounded-full border px-3.5 sm:px-4 py-2 text-[13px] sm:text-sm font-semibold ${
                          active
                            ? "border-emerald-300 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm"
                            : "border-stone-300 bg-white text-fg/80 hover:border-primary/35 hover:text-fg"
                        }`}
                        aria-pressed={active}
                      >
                        <span>{module}</span>
                        {active && (
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 border border-white/35">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="turnstile-wrap">
                <div ref={turnstileRef} />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-gradient w-full text-white font-semibold py-3.5 rounded-lg text-[14px] flex items-center justify-center gap-2.5 mt-2 tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <BoltIcon className="w-4 h-4" />
                {isSubmitting ? "Loading..." : lang === "id" ? "Daftar Kolabo" : "Register Kolabo"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => setTurnstileLoaded(true)}
      />
      {mounted && modalContent ? createPortal(modalContent, document.body) : null}
    </>
  );
}
